import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Row, Button } from 'react-bootstrap';
import isEqual from 'lodash/isEqual';
import FilterGroup from './FilterGroup';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import useActivityTemplateSearch from '../../../hooks/useActivityTemplateSearch';

// TODO: When finalized, move this into a table that can be managed, hard coded for now until we have final rules/configurations
const FILTER_GROUP_CONFIGS = {
    2: [ // Agent
        {
            key: 'InHouseAgentOrProspectStatusId',
            label: 'Status',
            options: [
                { id: 4, name: 'Active' },
                { id: 6, name: 'Severed' },
                { id: 9, name: 'In Process' },
            ],
        },
        {
            key: 'TransactionActingRoleId',
            label: 'Is Transaction Coordinator',
            options: [
                { id: 360, name: 'Only an Agent' },
                { id: 361, name: 'Agent or Transaction Coordinator' },
                { id: 362, name: 'Only a Transaction Coordinator' },
            ],
        },
        {
            key: 'TriggeredActivityTemplate',
            label: 'On Triggered Activity',
            dynamic: true,
            dynamicType: 'activityTemplate'
        },
    ],
    5: [ // Listing
        {
            key: 'TransactionListingStatusId',
            label: 'Status',
            options: [
                { id: 1, name: 'Pending Approval' },
                { id: 2, name: 'Active' },
                { id: 3, name: 'Expired' },
                { id: 5, name: 'Closed' },
                { id: 6, name: 'Pending' },
            ],
        },
        {
            key: 'TransactionListingTypeId',
            label: 'Listing Type',
            options: [
                { id: 1, name: 'Sale' },
                { id: 2, name: 'Rental' },
            ]
        },
        {
            key: 'FileCompleteStatusId',
            label: 'File Complete',
            options: [
                { id: 1, name: 'Complete' },
                { id: 2, name: 'Incomplete' },
            ],
        },
        {
            key: 'RequiredDocumentMissing',
            label: 'Required Documents Missing',
            options: [
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'PaymentDocumentMissing',
            label: 'Required Payment Documents Missing',
            options: [
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'FineDocumentMissing',
            label: 'Required Fine Documents Missing',
            options: [
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'HasIncompleteReviewItems',
            label: 'Has Incomplete Review Items',
            options: [
                { id: 1, name: 'Yes' },
            ],
        },
        {
            key: 'TriggeredActivityTemplate',
            label: 'On Triggered Activity',
            dynamic: true,
            dynamicType: 'activityTemplate'
        },
    ],
    6: [ // Sale
        {
            key: 'TransactionContractStatusId',
            label: 'Status',
            options: [
                { id: 1, name: 'Pending Approval' },
                { id: 2, name: 'Pending' },
                { id: 3, name: 'Closed' },
                { id: 5, name: 'Closed Unlocked' },
                { id: 7, name: 'Incomplete' },
            ],
        },
        {
            key: 'FileCompleteStatusId',
            label: 'File Complete',
            options: [
                { id: 1, name: 'Complete' },
                { id: 2, name: 'Incomplete' },
            ],
        },
        {
            key: 'TransactionPartySideId',
            label: 'Party Side',
            options: [
                { id: 1, name: 'Buyer' },
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'RequiredDocumentMissing',
            label: 'Required Documents Missing',
            options: [
                { id: 1, name: 'Buyer' },
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'PaymentDocumentMissing',
            label: 'Required Payment Documents Missing',
            options: [
                { id: 1, name: 'Buyer' },
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'FineDocumentMissing',
            label: 'Required Fine Documents Missing',
            options: [
                { id: 1, name: 'Buyer' },
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'HasIncompleteReviewItems',
            label: 'Has Incomplete Review Items',
            options: [
                { id: 1, name: 'Yes' },
            ],
        },
        {
            key: 'TriggeredActivityTemplate',
            label: 'On Triggered Activity',
            dynamic: true,
            dynamicType: 'activityTemplate'
        },
    ],
    8: [ // Rental
        {
            key: 'TransactionContractStatusId',
            label: 'Status',
            options: [
                { id: 1, name: 'Pending Approval' },
                { id: 2, name: 'Pending' },
                { id: 3, name: 'Closed' },
                { id: 5, name: 'Closed Unlocked' },
                { id: 7, name: 'Incomplete' },
            ],
        },
        {
            key: 'FileCompleteStatusId',
            label: 'File Complete',
            options: [
                { id: 1, name: 'Complete' },
                { id: 2, name: 'Incomplete' },
            ],
        },
        {
            key: 'TransactionPartySideId',
            label: 'Party Side',
            options: [
                { id: 1, name: 'Tenant' },
                { id: 2, name: 'Landlord' },
            ],
        },
        {
            key: 'RequiredDocumentMissing',
            label: 'Required Documents Missing',
            options: [
                { id: 1, name: 'Buyer' },
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'PaymentDocumentMissing',
            label: 'Required Payment Documents Missing',
            options: [
                { id: 1, name: 'Buyer' },
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'FineDocumentMissing',
            label: 'Required Fine Documents Missing',
            options: [
                { id: 1, name: 'Buyer' },
                { id: 2, name: 'Seller' },
            ],
        },
        {
            key: 'HasIncompleteReviewItems',
            label: 'Has Incomplete Review Items',
            options: [
                { id: 1, name: 'Yes' },
            ],
        },
        {
            key: 'TriggeredActivityTemplate',
            label: 'On Triggered Activity',
            dynamic: true,
            dynamicType: 'activityTemplate'
        },
    ],
    10: [ // MLS Listing
        {
            key: 'LastNoticeOn',
            label: 'Last Notice On',
            options: [
                { id: 1, name: 'Date Is Set' },
            ],
        }
    ],
    11: [ // MLS Off Market
        {
            key: 'LastNoticeOn',
            label: 'Last Notice On',
            options: [
                { id: 1, name: 'Date Is Set' },
            ],
        }
    ],
    // Buyer Broker (9) doesn't have filter groups yet
};

/*
Props:
- selectedRuleTypeId: number
- initialGroups: Array<{ id, filterKey, values: number[] }>
- isBusy: boolean
- onChange: (apiGroups) => void  // apiGroups: Array<{ id, filterKey, values }>
*/
function FilterGroups({ selectedRuleTypeId, initialGroups = [], isBusy, onChange, errorMessage }) {
    const availableConfigs = useMemo(() => FILTER_GROUP_CONFIGS[selectedRuleTypeId] || [], [selectedRuleTypeId]);
    const configByKey = useMemo(() => {
        const dict = {};
        (availableConfigs || []).forEach(cfg => { dict[cfg.key] = cfg; });
        return dict;
    }, [availableConfigs]);

    const [groups, setGroups] = useState([]); // [{id, filterKey, values:[{name,value}], config}]
    const prevApiRef = useRef([]);
    const internalChangeRef = useRef(false);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceRef = useRef(null);
    const { options: activityOptions, loading: activityLoading, error: activityError, search: activitySearch } = useActivityTemplateSearch();

    const toApi = (list) => (list || []).map(g => ({
        id: g.id ?? null,
        filterKey: g.filterKey,
        values: (g.values || []).map(v => ({ name: v.name, value: v.value }))
    }));

    // Initialize from initialGroups when configs change or initial changes
    useEffect(() => {
        // If this effect runs right after an internal change, ignore to avoid flipping UI
        if (internalChangeRef.current) {
            internalChangeRef.current = false;
            return;
        }
        const mapped = (initialGroups || [])
            .map(g => ({
                id: g.id ?? null,
                filterKey: g.filterKey,
                values: (g.values || []).map(v => ({ name: v.name, value: v.value })),
                config: configByKey[g.filterKey]
            }))
            .filter(g => !!g.config);
        // Avoid resetting state if no real change
        if (!isEqual(toApi(groups), toApi(mapped))) {
            setGroups(mapped);
        }
    }, [initialGroups, configByKey, groups]);

    useEffect(() => {
        if (!onChange) return;
        const apiGroups = toApi(groups);
        if (!isEqual(prevApiRef.current, apiGroups)) {
            prevApiRef.current = apiGroups;
            onChange(apiGroups);
        }
    }, [groups, onChange]);

    const addFilterGroup = (filterConfig) => {
        if (!filterConfig || groups.some(g => g.filterKey === filterConfig.key)) return;
        const base = { id: null, filterKey: filterConfig.key, values: [], config: filterConfig };
        const next = [...groups, base];
        internalChangeRef.current = true;
        setGroups(next);
    };

    const removeFilterGroup = (index) => {
        const next = groups.filter((_, i) => i !== index);
        internalChangeRef.current = true;
        setGroups(next);
    };

    const handleValuesChange = (index, selectedValues) => {
        const next = groups.map((g, i) => i === index ? {
            ...g,
            values: selectedValues.map(v => ({ name: v.name, value: v.id }))
        } : g);
        internalChangeRef.current = true;
        setGroups(next);
    };

    const handleDynamicSelect = (groupIndex, selection) => {
        if (!selection || selection.length === 0) return;
        const choice = selection[0];
        const label = choice.displayName || choice.name || '';
        const next = groups.map((g, i) => i === groupIndex ? {
            ...g,
            values: [{ name: label, value: choice.id }]
        } : g);
        internalChangeRef.current = true;
        setGroups(next);
        setSearchQuery(''); // clear search box so selected item chip shows
    };

    // Fire search for dynamic group when query changes (debounced)
    useEffect(() => {
        const dynamicGroup = groups.find(g => g.config?.dynamicType === 'activityTemplate');
        if (!dynamicGroup) return;
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        if (searchQuery && searchQuery.length >= 3) {
            debounceRef.current = setTimeout(() => {
                activitySearch(selectedRuleTypeId, searchQuery);
            }, 300);
        }
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [searchQuery, groups, activitySearch, selectedRuleTypeId]);

    const addableConfigs = (availableConfigs || []).filter(cfg => !groups.some(g => g.filterKey === cfg.key));

    return (
        <>
            <Row>
                <div className="col-md-12 mt-1">
                    <div style={{ borderBottom: '1px solid #b7b7bd', marginBottom: '20px' }}>
                        <h4><strong>Filter Conditions</strong></h4>
                    </div>
                </div>
            </Row>
            {errorMessage && (
                <Row>
                    <div className="col-md-12">
                        <div style={{ color: 'red', marginTop: '-10px', marginBottom: '10px' }}>{errorMessage}</div>
                    </div>
                </Row>
            )}
            <Row>
                <div className="col-md-12">
                    <div style={{ marginBottom: '20px' }}>
                        <strong>Add Filter:</strong>
                        {addableConfigs.map((config, index) => (
                            <Button
                                key={config.key}
                                bsStyle="primary"
                                size="sm"
                                disabled={isBusy}
                                onClick={() => addFilterGroup(config)}
                                style={{ marginLeft: index === 0 ? '10px' : '5px', marginRight: '5px' }}
                            >
                                <i className="fa fa-plus"></i> {config.label}
                            </Button>
                        ))}
                        {addableConfigs.length === 0 && (
                            <span style={{ marginLeft: '10px', color: '#666' }}>All available filters added</span>
                        )}
                    </div>

                    {groups.map((filterGroup, index) => {
                        if (filterGroup.config?.dynamicType === 'activityTemplate') {
                            const selectedDynamic = (filterGroup.values && filterGroup.values.length === 1)
                                ? [{ id: filterGroup.values[0].value, name: filterGroup.values[0].name, displayName: filterGroup.values[0].name }]
                                : [];
                            return (
                                <div key={`${filterGroup.filterKey}-${index}`} style={{ border: '1px solid #ddd', borderRadius: 4, padding: 15, marginBottom: 15, background: '#f9f9f9' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                                        <div style={{ flex: 1 }}>
                                            <strong>{filterGroup.config.label}</strong>
                                            <AsyncTypeahead
                                                id={`activity-template-${index}`}
                                                labelKey={(option) => option.displayName || option.name}
                                                minLength={3}
                                                isLoading={activityLoading}
                                                onSearch={(q) => setSearchQuery(q)}
                                                options={activityOptions}
                                                onChange={(sel) => handleDynamicSelect(index, sel)}
                                                placeholder="Search activity templates..."
                                                selected={selectedDynamic}
                                                renderMenuItemChildren={(option) => (
                                                    <div><span>{option.displayName || option.name}</span></div>
                                                )}
                                            />
                                            {activityError && <div style={{ color: 'red', marginTop: 5 }}>Error: {activityError}</div>}
                                        </div>
                                        <div>
                                            <Button bsStyle="danger" disabled={isBusy} onClick={() => removeFilterGroup(index)}>
                                                <i className="fa fa-trash"></i>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <FilterGroup
                                key={`${filterGroup.filterKey}-${index}`}
                                filterGroup={filterGroup}
                                index={index}
                                isBusy={isBusy}
                                onValuesChange={handleValuesChange}
                                onRemove={removeFilterGroup}
                            />
                        );
                    })}

                    {groups.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            color: '#666',
                            fontStyle: 'italic',
                            padding: '20px',
                            border: '2px dashed #ccc',
                            borderRadius: '4px'
                        }}>
                            No filter conditions added. Click the buttons above to add filters.
                        </div>
                    )}
                </div>
            </Row>
        </>
    );
}

export default FilterGroups;
